import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProductSearchResult {
  master_product_id: string | null;
  identifiers: Array<{ identifier_type: string; identifier_value: string }>;
  preferred_identifier_value?: string;
  preferred_identifier_type?: string;
}

interface IdentifierLinkRow {
  master_product_id: string | null;
  identifier_type: string;
  identifier_value: string;
}

interface MappingRow {
  primary_type: string;
  primary_value: string;
  alias_type: string;
  alias_value: string;
}

const SEARCH_LIMIT = 20;

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const normalizeType = (value: string) => value.toLowerCase().trim();
const normalizeIdentifierValue = (value: string) => value.trim().normalize("NFC");

// Identifier types are categorical; values preserve case-sensitive identity.
const identifierKey = (identifierType: string, identifierValue: string) =>
  `${normalizeType(identifierType)}::${normalizeIdentifierValue(identifierValue)}`;

export function mergeSearchResultsWithMappings(
  rows: IdentifierLinkRow[],
  mappings: MappingRow[],
  primaryRows: IdentifierLinkRow[] = [],
): ProductSearchResult[] {
  const aliasToPrimary = new Map<string, { type: string; value: string }>();
  for (const mapping of mappings) {
    aliasToPrimary.set(identifierKey(mapping.alias_type, mapping.alias_value), {
      type: mapping.primary_type,
      value: mapping.primary_value,
    });
  }

  const canonicalToMasterProductIdExact = new Map<string, string | null>();
  for (const row of [...rows, ...primaryRows]) {
    canonicalToMasterProductIdExact.set(
      identifierKey(row.identifier_type, row.identifier_value),
      row.master_product_id,
    );
  }

  const grouped = new Map<string, ProductSearchResult>();
  for (const row of rows) {
    const aliasLookupKey = identifierKey(row.identifier_type, row.identifier_value);
    const mappedPrimary = aliasToPrimary.get(aliasLookupKey);
    const canonicalType = mappedPrimary?.type ?? row.identifier_type;
    const canonicalValue = mappedPrimary?.value ?? row.identifier_value;
    const canonicalKey = identifierKey(canonicalType, canonicalValue);
    // Explicit mappings apply only to the exact observed identifier value.
    const resolvedMasterProductId =
      canonicalToMasterProductIdExact.get(canonicalKey) ??
      row.master_product_id;

    // UC-163 Work Item 4: Group by identifier key for unlinked identifiers (null master_product_id)
    const groupKey = resolvedMasterProductId ?? canonicalKey;

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        master_product_id: resolvedMasterProductId,
        identifiers: [],
        preferred_identifier_type: canonicalType,
        preferred_identifier_value: canonicalValue,
      });
    }

    grouped.get(groupKey)!.identifiers.push({
      identifier_type: row.identifier_type,
      identifier_value: row.identifier_value,
    });
  }

  return Array.from(grouped.values()).slice(0, SEARCH_LIMIT);
}

// UC-163 Work Item 4: Unmatched inventory visibility and Inventory → Adjust search
// Covers UC-163 · AC-UC163-17 · AC-UC163-18 · AC-UC163-19 · AC-UC163-20 · AC-UC163-21
export function useProductSearch(organisationId: string | undefined, term: string) {
  const trimmed = term.trim();
  const debounced = useDebounced(trimmed, 200);

  return useQuery({
    queryKey: ["product-search", organisationId, debounced],
    enabled: !!organisationId && debounced.length >= 1,
    queryFn: async (): Promise<ProductSearchResult[]> => {
      const supabaseAny = supabase as any;

      // Query both linked identifiers and unlinked observations
      // UC-163 Work Item 4: Do not filter by source — users need to find ALL products
      // in their org for adjustment, regardless of identifier provenance
      const linkedQuery = supabaseAny
        .from("product_identifier_links")
        .select("master_product_id, identifier_type, identifier_value")
        .eq("organisation_id", organisationId!)
        .ilike("identifier_value", `%${debounced.toLowerCase()}%`)
        .limit(SEARCH_LIMIT * 5);

      const { data: linkedData, error: linkedError } = await linkedQuery;

      if (linkedError) throw new Error(`/errors/inventory/identifier-search-failure: ${linkedError.message}`);

      const initialRows: IdentifierLinkRow[] = linkedData ?? [];

      // Query unlinked observations (identifiers with no master_product_id mapping)
      // Use actual column names from identifier_observation table
      const unlinkedQuery = supabaseAny
        .from("identifier_observation")
        .select("interpreted_identifier_type, raw_as_observed_value")
        .eq("organisation_id", organisationId!)
        .ilike("raw_as_observed_value", `%${debounced.toLowerCase()}%`)
        .limit(SEARCH_LIMIT * 5);

      const { data: unlinkedData, error: unlinkedError } = await unlinkedQuery;

      if (unlinkedError) throw new Error(`/errors/inventory/identifier-search-failure: ${unlinkedError.message}`);

      // Add unlinked observations as entries with null master_product_id
      const unlinkedRows: IdentifierLinkRow[] = (unlinkedData ?? []).map((obs: { interpreted_identifier_type: string; raw_as_observed_value: string }) => ({
        master_product_id: null,
        identifier_type: obs.interpreted_identifier_type,
        identifier_value: obs.raw_as_observed_value,
      }));

      // Filter out unlinked observations that are already present in linked
      // results. This identity key is case-sensitive so "apple" cannot
      // collide with an unrelated linked "Apple" row (AC-UC162-16).
      const linkedKeys = new Set(
        initialRows.map((row) => identifierKey(row.identifier_type, row.identifier_value)),
      );
      const uniqueUnlinkedRows = unlinkedRows.filter(
        (row) => !linkedKeys.has(identifierKey(row.identifier_type, row.identifier_value)),
      );

      const allRows = [...initialRows, ...uniqueUnlinkedRows];

      const matchedIdentifierValues = Array.from(
        new Set(allRows.map((row) => normalizeIdentifierValue(row.identifier_value))),
      );

      let mappings: MappingRow[] = [];
      if (matchedIdentifierValues.length > 0) {
        const { data: mappingsData, error: mappingsError } = await supabaseAny
          .from("product_mappings")
          .select("primary_type, primary_value, alias_type, alias_value")
          .eq("organisation_id", organisationId!)
          .is("effective_to", null)
          .in("alias_value", matchedIdentifierValues);

        if (mappingsError) throw new Error(mappingsError.message);
        mappings = mappingsData ?? [];
      }

      const initialCanonicalKeys = new Set(allRows.map((row) => identifierKey(row.identifier_type, row.identifier_value)));
      const missingPrimaryValues = new Set<string>();
      for (const mapping of mappings) {
        const aliasKey = identifierKey(mapping.alias_type, mapping.alias_value);
        if (initialCanonicalKeys.has(aliasKey)) {
          const primaryKey = identifierKey(mapping.primary_type, mapping.primary_value);
          if (!initialCanonicalKeys.has(primaryKey)) {
            missingPrimaryValues.add(normalizeIdentifierValue(mapping.primary_value));
          }
        }
      }

      let primaryRows: IdentifierLinkRow[] = [];
      if (missingPrimaryValues.size > 0) {
        const { data: primaryData, error: primaryError } = await supabaseAny
          .from("product_identifier_links")
          .select("master_product_id, identifier_type, identifier_value")
          .in("identifier_value", Array.from(missingPrimaryValues))
          .eq("organisation_id", organisationId!);

        if (primaryError) throw new Error(primaryError.message);
        primaryRows = primaryData ?? [];
      }

      return mergeSearchResultsWithMappings(allRows, mappings, primaryRows);
    },
  });
}
